import React, { useState, useEffect } from 'react';
import { Grid, IconButton, Button, Typography, Box } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from 'icons/materialIcons';
import GetStarted from '../components/GetStarted';
import CompanyInfo from '../components/CompanyInfo';
import CardDetails from '../components/CardDetails';
import SignUp from '../components/SignUp';
import DigitalCardPreview from '../components/DigitalCardPreview';
import { useUserContext } from '../contexts/UserContext';
import { useCardContext } from '../contexts/CardContext';

export default function LoginHome() {
  const { userState } = useUserContext(); // Get userState from UserContext
  const { cardState } = useCardContext(); // Get cardState from CardContext

  // Load currentPage from localStorage or default to 1
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('currentPage');
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [showPreview, setShowPreview] = useState(() => {
    // Determine if preview should be shown based on the savedPage
    return localStorage.getItem('currentPage') === '2';
  });

  useEffect(() => {
    // Save currentPage to localStorage whenever it changes
    localStorage.setItem('currentPage', currentPage);
    // Update showPreview based on the current page
    if (currentPage >= 2) {
      setShowPreview(true);
    } else {
      setShowPreview(false);
    }
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNext = () => {
    if (currentPage < 4) {
      if (currentPage === 1 && !userState.userName) return; // Ensure userName is not empty
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, height: '100vh' }}>
      <Grid container columns={16} sx={{ height: '100%' }}>
        <Grid
          item
          xs={12}
          sm={7}
          md={7}
          sx={{
            backgroundColor: 'white',
            height: '100%',
            borderTopRightRadius: '26px',
            borderBottomRightRadius: '26px',
            padding: '54px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: 'border-box',
            width: '100%',
            flex: '0 0 50%'
          }}
        >
          <Box sx={{ marginBottom: '16px', display: 'flex', alignItems: 'center', width: '100%' }}>
            <IconButton
              onClick={handlePrevious}
              disabled={currentPage === 1}
              sx={{ marginRight: '8px' }}
            >
              <ArrowBackIos />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}>
              {[1, 2, 3, 4].map((page) => (
                <Box
                  key={page}
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: '0 16px',
                  }}
                >
                  <Button
                    variant="contained"
                    color={currentPage >= page ? 'primary' : 'default'}
                    sx={{
                      backgroundColor: currentPage >= page ? 'black' : '#d3d3d3',
                      color: 'white',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      padding: 0,
                      minWidth: 0,
                      marginBottom: '4px',
                      pointerEvents: page === 1 || userState.userName ? 'auto' : 'none',
                      opacity: page === 1 || userState.userName ? 1 : 0.5,
                    }}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Button>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {page === 1 && 'Get Started'}
                    {page === 2 && 'Company Info'}
                    {page === 3 && 'Card Details'}
                    {page === 4 && 'Get Your Digital Card'}
                  </Typography>
                </Box>
              ))}
            </Box>

            <IconButton
              onClick={handleNext}
              disabled={currentPage === 4 || (!userState.userName && currentPage === 1)}
              sx={{ marginLeft: '8px' }}
            >
              <ArrowForwardIos />
            </IconButton>
          </Box>

          <Box sx={{ flexGrow: 1, width: '100%', minHeight: '500px' }}>
            {currentPage === 1 && (
              <GetStarted onContinue={handleNext} />
            )}
            {currentPage === 2 && (
              <CompanyInfo
                onContinue={() => setCurrentPage(3)}
              />
            )}
            {currentPage === 3 && (
              <CardDetails
                onContinue={() => setCurrentPage(4)}
              />
            )}
            {currentPage === 4 && (
              <SignUp
                onContinue={handleNext} // Pass onContinue to SignUp component
              />
            )}
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          sm={5}
          md={9}
          sx={{
            backgroundColor: '#f1f1f1',
            height: '100%',
            padding: '20px',
          }}
        >
          {showPreview && (
            <DigitalCardPreview
              name={userState.userName} // Pass userName to DigitalCardPreview
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
